import React, { useState, useEffect } from 'react';
import { Snackbar, Alert, Button, Box } from '@mui/material';
import { performCompleteUpdate } from '../utils/pwaUpdateUtils';

interface UpdateNotificationProps {
  onUpdateAccepted?: () => void;
}

const UpdateNotification: React.FC<UpdateNotificationProps> = ({ onUpdateAccepted }) => {
  const [showUpdate, setShowUpdate] = useState(false);
  const [newVersion, setNewVersion] = useState<string>('');
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    // Check if service worker is supported
    if ('serviceWorker' in navigator) {
      // Listen for service worker updates
      navigator.serviceWorker.addEventListener('message', (event) => {
        if (event.data && event.data.type === 'SW_UPDATED') {
          setNewVersion(event.data.version);
          setShowUpdate(true);
        }

        if (event.data && event.data.type === 'CACHES_CLEARED') {
          console.log('All caches cleared, reloading...');
          window.location.reload();
        }
      });

      // Check for updates periodically
      const checkForUpdates = () => {
        navigator.serviceWorker.getRegistration().then((registration) => {
          if (registration) {
            registration.update();
          }
        });
      };

      // Check for updates every 10 minutes
      const updateInterval = setInterval(checkForUpdates, 10 * 60 * 1000);

      // Check for updates on page focus
      const handleFocus = () => {
        checkForUpdates();
      };

      window.addEventListener('focus', handleFocus);

      return () => {
        clearInterval(updateInterval);
        window.removeEventListener('focus', handleFocus);
      };
    }
    return undefined;
  }, []);

  const handleUpdateAccept = async () => {
    setIsUpdating(true);

    if (onUpdateAccepted) {
      onUpdateAccepted();
    }

    try {
      console.log('Starting complete PWA update...');
      await performCompleteUpdate();
    } catch (error) {
      console.error('Error during update process:', error);
      setIsUpdating(false);
      // Fallback to hard reload
      window.location.href = `${window.location.href}?v=${Date.now()}`;
    }
  };

  const handleUpdateDismiss = () => {
    if (!isUpdating) {
      setShowUpdate(false);
    }
  };

  return (
    <Snackbar
      open={showUpdate}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      sx={{
        '& .MuiSnackbarContent-root': {
          padding: 0
        }
      }}
    >
      <Alert
        severity="info"
        sx={{
          width: '100%',
          '& .MuiAlert-message': {
            width: '100%'
          }
        }}
        action={(
          <Box sx={{ display: 'flex', gap: 1, ml: 2 }}>
            <Button
              color="primary"
              size="small"
              onClick={handleUpdateDismiss}
              disabled={isUpdating}
              variant="outlined"
            >
              Later
            </Button>
            <Button
              color="primary"
              size="small"
              onClick={handleUpdateAccept}
              disabled={isUpdating}
              variant="contained"
            >
              {isUpdating ? 'Updating...' : 'Update Now'}
            </Button>
          </Box>
        )}
      >
        <Box>
          <strong>New version available!</strong>
          <br />
          <small>
            {isUpdating
              ? 'Clearing cache and updating to the latest version...'
              : `Version ${newVersion} is ready. This will clear all cached data and restart the app.`
            }
          </small>
        </Box>
      </Alert>
    </Snackbar>
  );
};

export default UpdateNotification;
